import React from 'react';
import styled from 'styled-components';
import Section, { SectionTone } from './ui/Section';
import Container from './ui/Container';
import SectionHeading from './ui/SectionHeading';
import Reveal from './ui/Reveal';
import { processSteps } from '../data/process';

const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.5rem;
  counter-reset: step;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1.5rem 1.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--border);
  background: var(--surface);
`;

const Number = styled.span`
  font-family: var(--font-display);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--accent);
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border-strong);
  }
`;

const StepTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.6rem;
`;

const StepText = styled.p`
  color: var(--text-2);
  font-size: 0.95rem;
  flex-grow: 1;
`;

const Artifact = styled.p`
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border);
  font-size: var(--fs-small);
  color: var(--text-2);

  span {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-3);
    margin-bottom: 0.25rem;
  }
`;

interface ProcessProps {
  tone?: SectionTone;
  title?: string;
  description?: string;
}

const Process: React.FC<ProcessProps> = ({
  tone = 'raised',
  title = 'How a project runs',
  description = 'Four steps from first call to production, and we stay on after launch.',
}) => (
  <Section $tone={tone} aria-labelledby="process-title">
    <Container>
      <SectionHeading id="process-title" eyebrow="Process" title={title} description={description} />
      <Steps>
        {processSteps.map((step, index) => (
          <Reveal as="li" key={step.title} delay={index * 0.06}>
            <Step>
              <Number aria-hidden="true">{String(index + 1).padStart(2, '0')}</Number>
              <StepTitle>{step.title}</StepTitle>
              <StepText>{step.description}</StepText>
              <Artifact>
                <span>You get</span>
                {step.artifact}
              </Artifact>
            </Step>
          </Reveal>
        ))}
      </Steps>
    </Container>
  </Section>
);

export default Process;
